import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useI18n, type StringKey } from "../i18n";
import {
  addPayAdjustment, approvePayroll, calculatePayroll, createPayrollRun, deletePayrollRun, getSetting, loadPayAdjustments, loadApprovalsCount,
  loadDirectory, loadPayElements, loadPayItems, loadPayrollProfiles, loadPayrollRuns, loadPayslips, loadRunTotals, markPayrollPaid,
  removePayAdjustment, reopenPayroll, savePayElement, savePayrollProfile, saveSetting, setPayItem,
  type Adjustment, type PayElement, type PayItem, type PayrollProfile, type PayrollRun, type Payslip,
} from "../data";
import { money, printPayslip } from "../lib/payslip";
import { downloadText, toCsv } from "../lib/csv";
import type { DirectoryRow, Workspace } from "../types";

type Tab = "runs" | "setup" | "elements" | "settings";
const errText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const statusTone: Record<string, string> = { draft: "neutral", calculated: "warning", approved: "info", paid: "success" };

/** Payroll: monthly runs with approval, each employee's pay, pay elements and settings. */
export function Payroll({ ws }: { ws: Workspace }) {
  const { t } = useI18n();
  const [tab, setTab] = useState<Tab>("runs");
  const [openRun, setOpenRun] = useState<string | null>(null);
  const manage = ws.permissions.has("payroll.manage");
  const tabs: [Tab, StringKey][] = [["runs", "payRuns"], ["setup", "paySetup"], ["elements", "payElements"]];
  if (ws.permissions.has("settings.manage")) tabs.push(["settings", "navSettings"]);
  return (
    <div className="page">
      <div className="tabs" role="tablist">
        {tabs.map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? "tab current" : "tab"}
            onClick={() => { setTab(k); setOpenRun(null); }}>{t(label)}</button>
        ))}
      </div>
      {tab === "runs" && (openRun ? <RunView ws={ws} runId={openRun} onBack={() => setOpenRun(null)} /> : <Runs ws={ws} onOpen={setOpenRun} />)}
      {tab === "setup" && <PaySetup ws={ws} canEdit={manage} />}
      {tab === "elements" && <Elements ws={ws} canEdit={manage} />}
      {tab === "settings" && <PayrollSettings ws={ws} />}
    </div>
  );
}

function useMonthName() {
  const { lang } = useI18n();
  return (period: string) => new Date(`${period}T00:00:00`).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-GB", { month: "long", year: "numeric" });
}

function Runs({ ws, onOpen }: { ws: Workspace; onOpen: (id: string) => void }) {
  const { t } = useI18n();
  const monthName = useMonthName();
  const cid = ws.membership.company_id;
  const [runs, setRuns] = useState<PayrollRun[] | null>(null);
  const [totals, setTotals] = useState<Map<string, { n: number; net: number; cost: number }>>(new Map());
  const [month, setMonth] = useState(today().slice(0, 7));
  const [error, setError] = useState<string | null>(null);
  const refresh = async () => {
    try {
      setRuns(await loadPayrollRuns(cid));
      const m = new Map<string, { n: number; net: number; cost: number }>();
      for (const s of await loadRunTotals(cid)) {
        const x = m.get(s.run_id) ?? { n: 0, net: 0, cost: 0 };
        m.set(s.run_id, { n: x.n + 1, net: x.net + Number(s.net), cost: x.cost + Number(s.employer_cost) });
      }
      setTotals(m);
    } catch (e) { setError(errText(e, t("errorGeneric"))); }
  };
  useEffect(() => { void refresh(); }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps

  async function create(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try { const id = await createPayrollRun(cid, `${month}-01`); onOpen(id); }
    catch (err) { setError(errText(err, t("errorGeneric"))); }
  }

  return (
    <>
      {ws.permissions.has("payroll.manage") && (
        <form className="card" onSubmit={create}>
          <h2>{t("payNewRun")}</h2>
          <div className="row">
            <input type="month" aria-label={t("dashMonth")} value={month} onChange={(e) => setMonth(e.target.value)} required />
            <button className="btn primary">{t("payPrepare")}</button>
          </div>
        </form>
      )}
      {error && <p className="alert danger" role="alert">{error}</p>}
      <section className="card">
        <h2>{t("payRuns")}</h2>
        {runs === null ? <p className="muted">{t("loading")}</p> : runs.length === 0 ? <p className="muted">{t("payNoRuns")}</p> : (
          <div className="table-wrap"><table>
            <thead><tr><th>{t("dashMonth")}</th><th>{t("status")}</th><th className="hide-sm">{t("payPeople")}</th><th>{t("payNet")}</th><th className="hide-sm">{t("payEmployerCost")}</th></tr></thead>
            <tbody>{runs.map((r) => {
              const x = totals.get(r.id);
              return (
                <tr key={r.id}>
                  <td><button type="button" className="link strong" onClick={() => onOpen(r.id)}>{monthName(r.period)}</button></td>
                  <td><span className={`badge ${statusTone[r.status]}`}>{t(`payStatus_${r.status}` as StringKey)}</span></td>
                  <td className="hide-sm mono">{x?.n ?? 0}</td>
                  <td className="mono">{x ? money(x.net) : "—"}</td>
                  <td className="hide-sm mono">{x ? money(x.cost) : "—"}</td>
                </tr>
              );
            })}</tbody>
          </table></div>
        )}
      </section>
    </>
  );
}

function RunView({ ws, runId, onBack }: { ws: Workspace; runId: string; onBack: () => void }) {
  const { t, pick } = useI18n();
  const monthName = useMonthName();
  const cid = ws.membership.company_id;
  const company = ws.membership.company;
  const can = (p: string) => ws.permissions.has(p);
  const [run, setRun] = useState<PayrollRun | null>(null);
  const [slips, setSlips] = useState<Payslip[]>([]);
  const [adjs, setAdjs] = useState<Adjustment[]>([]);
  const [elements, setElements] = useState<PayElement[]>([]);
  const [people, setPeople] = useState<DirectoryRow[]>([]);
  const [approvals, setApprovals] = useState(0);
  const [needed, setNeeded] = useState(1);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const refresh = async () => {
    const runs = await loadPayrollRuns(cid);
    setRun(runs.find((r) => r.id === runId) ?? null);
    setSlips(await loadPayslips(runId));
    setAdjs(await loadPayAdjustments(runId));
    setApprovals((await loadApprovalsCount(runId)).length);
  };
  useEffect(() => {
    void refresh().catch((e) => setMsg({ ok: false, text: errText(e, t("errorGeneric")) }));
    void loadPayElements(cid).then(setElements);
    void loadDirectory(cid).then(setPeople);
    void getSetting(cid, "payroll.approval_levels").then((v) => setNeeded(Number(v ?? 1)));
  }, [runId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function act(fn: () => Promise<unknown>, done?: StringKey) {
    setBusy(true);
    setMsg(null);
    try { await fn(); await refresh(); if (done) setMsg({ ok: true, text: t(done) }); }
    catch (e) { setMsg({ ok: false, text: errText(e, t("errorGeneric")) }); }
    finally { setBusy(false); }
  }

  const totals = useMemo(() => slips.reduce((a, s) => ({
    gross: a.gross + Number(s.gross), net: a.net + Number(s.net), si_ee: a.si_ee + Number(s.si_employee),
    si_er: a.si_er + Number(s.si_employer), tax: a.tax + Number(s.income_tax), cost: a.cost + Number(s.employer_cost),
    fund: a.fund + Number(s.martyrs_fund),
  }), { gross: 0, net: 0, si_ee: 0, si_er: 0, tax: 0, cost: 0, fund: 0 }), [slips]);

  if (!run) return <p className="muted">{t("loading")}</p>;
  const editable = run.status === "draft" || run.status === "calculated";
  const stale = run.status === "calculated" && adjs.some((a) => run.calculated_at && a.created_at > run.calculated_at);
  const noPay = slips.filter((s) => Number(s.gross) === 0);
  const shown = slips.filter((s) => !q || `${s.full_name} ${s.full_name_ar ?? ""} ${s.employee_number}`.toLowerCase().includes(q.toLowerCase()));
  const el = (id: string) => elements.find((x) => x.id === id);
  const person = (id: string) => people.find((p) => p.employment_id === id);
  const file = (name: string) => `${name}-${run.period.slice(0, 7)}.csv`;

  function print(s: Payslip) {
    printPayslip(s, { company: company.name, companyAr: company.name_ar ?? "", period: run!.period, payDate: run!.pay_date });
  }
  const exportBank = () => downloadText(file("bank-transfer"), toCsv(
    ["Employee number", "Name", "Bank", "Account / IBAN", "Net pay (EGP)"],
    slips.filter((s) => s.payment_method === "bank" && Number(s.net) > 0).map((s) => [s.employee_number, s.full_name, s.bank_name ?? "", s.bank_account ?? "", Number(s.net).toFixed(2)]),
  ));
  const exportInsurance = () => downloadText(file("social-insurance"), toCsv(
    ["Employee number", "Name", "Insurance number", "Insurable wage", "Employee share 11%", "Employer share 18.75%", "Total"],
    slips.filter((s) => Number(s.insurable_wage) > 0).map((s) => [s.employee_number, s.full_name, s.insurance_number ?? "", Number(s.insurable_wage).toFixed(2),
      Number(s.si_employee).toFixed(2), Number(s.si_employer).toFixed(2), (Number(s.si_employee) + Number(s.si_employer)).toFixed(2)]),
  ));
  const exportSummary = () => downloadText(file("payroll"), toCsv(
    ["Employee number", "Name", "Department", "Job", "Days paid", "Unpaid days", "Gross", "Insurable wage", "Social insurance (employee)",
     "Social insurance (employer)", "Health (employee)", "Income tax", "Martyrs' Fund", "Other deductions", "Net", "Employer cost", "Payment", "Bank", "Account"],
    slips.map((s) => [s.employee_number, s.full_name, s.department_name ?? "", s.job_title ?? "", String(s.days_paid), String(s.unpaid_days),
      ...[s.gross, s.insurable_wage, s.si_employee, s.si_employer, s.health_employee, s.income_tax, s.martyrs_fund, s.other_deductions, s.net, s.employer_cost].map((v) => Number(v).toFixed(2)),
      s.payment_method ?? "", s.bank_name ?? "", s.bank_account ?? ""]),
  ));

  return (
    <>
      <button type="button" className="link back" onClick={onBack}>← {t("payRuns")}</button>
      <section className="card">
        <div className="row spread">
          <h2>{monthName(run.period)} <span className={`badge ${statusTone[run.status]}`}>{t(`payStatus_${run.status}` as StringKey)}</span></h2>
          <span className="muted small-note">{t("payPayDate")}: <span className="mono">{run.pay_date ?? "—"}</span></span>
        </div>
        <ol className="stepper pay-steps">
          {(["draft", "calculated", "approved", "paid"] as const).map((s, i) => (
            <li key={s} className={["draft", "calculated", "approved", "paid"].indexOf(run.status) >= i ? "done" : ""}>{t(`payStatus_${s}` as StringKey)}</li>
          ))}
        </ol>
        {msg && <p className={`alert ${msg.ok ? "info" : "danger"}`} role="status">{msg.text}</p>}
        {stale && <p className="alert warning">{t("payStale")}</p>}
        {run.status === "calculated" && needed > 1 && <p className="muted small-note">{t("payApprovals").replace("{n}", String(approvals)).replace("{m}", String(needed))}</p>}
        <div className="row">
          {editable && can("payroll.manage") && (
            <button type="button" className="btn primary" disabled={busy} onClick={() => act(() => calculatePayroll(runId), "payCalculated")}>
              {run.status === "draft" ? t("payCalculate") : t("payRecalculate")}</button>
          )}
          {run.status === "calculated" && can("payroll.approve") && (
            <button type="button" className="btn primary" disabled={busy || stale} onClick={() => act(() => approvePayroll(runId))}>{t("approve")}</button>
          )}
          {run.status === "approved" && can("payroll.approve") && (
            <>
              <button type="button" className="btn primary" disabled={busy} onClick={() => act(() => markPayrollPaid(runId, today()), "payMarkedPaid")}>{t("payMarkPaid")}</button>
              <button type="button" className="btn secondary" disabled={busy} onClick={() => act(() => reopenPayroll(runId))}>{t("payReopen")}</button>
            </>
          )}
          {editable && can("payroll.manage") && (
            <button type="button" className="btn secondary" disabled={busy} onClick={async () => {
              if (!window.confirm(t("payDeleteConfirm"))) return;
              try { await deletePayrollRun(runId); onBack(); } catch (e) { setMsg({ ok: false, text: errText(e, t("errorGeneric")) }); }
            }}>{t("payDelete")}</button>
          )}
        </div>
      </section>

      {slips.length > 0 && (
        <div className="kpis">
          {([["payPeople", String(slips.length)], ["payGross", money(totals.gross)], ["payNet", money(totals.net)], ["payTax", money(totals.tax)],
             ["paySiTotal", money(totals.si_ee + totals.si_er)], ["payEmployerCost", money(totals.cost)]] as [StringKey, string][]).map(([k, v]) => (
            <div key={k} className="stat"><span className="stat-label">{t(k)}</span><span className="stat-value small mono">{v}</span></div>
          ))}
        </div>
      )}

      {editable && can("payroll.manage") && <AdjustmentForm runId={runId} people={people} elements={elements.filter((e) => e.active)} onAdded={refresh} />}
      {adjs.length > 0 && (
        <section className="card">
          <h2>{t("payAdjustments")}</h2>
          <ul className="req-list">
            {adjs.map((a) => {
              const p = person(a.employment_id);
              const x = el(a.element_id);
              return (
                <li key={a.id}>
                  <span><strong>{p ? pick(p.full_name, p.full_name_ar) : "—"}</strong> · {x ? pick(x.name_en, x.name_ar) : ""}{a.note ? ` · ${a.note}` : ""}</span>
                  <span className="row">
                    <span className={`mono ${x?.kind === "deduction" ? "danger-text" : ""}`}>{x?.kind === "deduction" ? "−" : "+"}{money(a.amount)}</span>
                    {editable && can("payroll.manage") && <button type="button" className="btn secondary small" onClick={() => act(() => removePayAdjustment(a.id))}>{t("payRemove")}</button>}
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {slips.length > 0 && (
        <section className="card">
          <div className="card-head">
            <h2>{t("payPayslips")}</h2>
            <div className="row">
              <button type="button" className="btn secondary small" onClick={exportSummary}>{t("payExportSummary")}</button>
              <button type="button" className="btn secondary small" onClick={exportBank}>{t("payExportBank")}</button>
              <button type="button" className="btn secondary small" onClick={exportInsurance}>{t("payExportInsurance")}</button>
            </div>
          </div>
          {noPay.length > 0 && <p className="alert warning">{t("payNoPaySet").replace("{n}", String(noPay.length))}: {noPay.map((s) => s.full_name).join(", ")}</p>}
          <input className="search" type="search" placeholder={t("search")} aria-label={t("search")} value={q} onChange={(e) => setQ(e.target.value)} />
          <div className="table-wrap"><table>
            <thead><tr><th>{t("employee")}</th><th>{t("payGross")}</th><th className="hide-sm">{t("payInsurance")}</th><th className="hide-sm">{t("payTax")}</th><th>{t("payNet")}</th><th /></tr></thead>
            <tbody>{shown.map((s) => (
              <tr key={s.id}>
                <td><span className="strong">{pick(s.full_name, s.full_name_ar)}</span><br /><span className="muted small-note">{s.employee_number}{s.unpaid_days ? ` · ${s.unpaid_days} ${t("payUnpaidDays")}` : ""}{Number(s.days_paid) + Number(s.unpaid_days) < s.days_in_month ? ` · ${t("payPartMonth")}` : ""}</span></td>
                <td className="mono">{money(s.gross)}</td>
                <td className="hide-sm mono">{money(s.si_employee)}</td>
                <td className="hide-sm mono">{money(s.income_tax)}</td>
                <td className="mono strong">{money(s.net)}</td>
                <td className="cell-actions"><button type="button" className="btn secondary small" onClick={() => print(s)}>{t("payPayslip")}</button></td>
              </tr>
            ))}</tbody>
          </table></div>
          <p className="muted small-note">{t("payRulesNote")}</p>
        </section>
      )}
    </>
  );
}

function AdjustmentForm({ runId, people, elements, onAdded }: {
  runId: string; people: DirectoryRow[]; elements: PayElement[]; onAdded: () => Promise<void>;
}) {
  const { t, pick } = useI18n();
  const [f, setF] = useState({ emp: "", el: "", amount: "", note: "" });
  const [error, setError] = useState<string | null>(null);
  const oneOff = elements.filter((e) => !e.recurring);
  async function add(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try { await addPayAdjustment(runId, f.emp, f.el, Number(f.amount), f.note); setF({ emp: "", el: "", amount: "", note: "" }); await onAdded(); }
    catch (err) { setError(errText(err, t("errorGeneric"))); }
  }
  return (
    <form className="card" onSubmit={add}>
      <h2>{t("payAddAdjustment")}</h2>
      <div className="field-row">
        <label className="field"><span>{t("employee")}</span>
          <select value={f.emp} onChange={(e) => setF({ ...f, emp: e.target.value })} required>
            <option value="">—</option>
            {people.filter((p) => p.status !== "terminated" && p.status !== "starting").map((p) => <option key={p.employment_id} value={p.employment_id}>{pick(p.full_name, p.full_name_ar)}</option>)}
          </select></label>
        <label className="field"><span>{t("payElement")}</span>
          <select value={f.el} onChange={(e) => setF({ ...f, el: e.target.value })} required>
            <option value="">—</option>
            {oneOff.map((x) => <option key={x.id} value={x.id}>{pick(x.name_en, x.name_ar)} ({t(x.kind === "earning" ? "payEarning" : "payDeduction")})</option>)}
          </select></label>
        <label className="field"><span>{t("payAmount")}</span><input type="number" min="0.01" step="0.01" inputMode="decimal" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} required /></label>
        <label className="field"><span>{t("note")}</span><input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></label>
      </div>
      {error && <p className="alert danger" role="alert">{error}</p>}
      <div className="actions"><button className="btn secondary">{t("payAdd")}</button></div>
      <p className="muted small-note">{t("payAdjHelp")}</p>
    </form>
  );
}

function PaySetup({ ws, canEdit }: { ws: Workspace; canEdit: boolean }) {
  const { t, pick } = useI18n();
  const cid = ws.membership.company_id;
  const [people, setPeople] = useState<DirectoryRow[]>([]);
  const [items, setItems] = useState<PayItem[]>([]);
  const [elements, setElements] = useState<PayElement[]>([]);
  const [profiles, setProfiles] = useState<Map<string, PayrollProfile>>(new Map());
  const [open, setOpen] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const refresh = async () => {
    const [p, i, e, pr] = await Promise.all([loadDirectory(cid), loadPayItems(cid), loadPayElements(cid), loadPayrollProfiles(cid)]);
    setPeople(p.filter((x) => x.status !== "terminated"));
    setItems(i);
    setElements(e);
    setProfiles(new Map(pr.map((x) => [x.employment_id, x])));
  };
  useEffect(() => { void refresh(); }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps
  const now = today();
  const current = (p: DirectoryRow) => {
    const day = p.hire_date > now ? p.hire_date : now;
    return items.filter((i) => i.employment_id === p.employment_id && i.start_date <= day && (!i.end_date || i.end_date >= day));
  };
  const elById = new Map(elements.map((e) => [e.id, e]));
  const shown = people.filter((p) => !q || `${p.full_name} ${p.full_name_ar ?? ""} ${p.employee_number}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <section className="card">
      <h2>{t("paySetup")}</h2>
      <input className="search" type="search" placeholder={t("search")} aria-label={t("search")} value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="table-wrap"><table>
        <thead><tr><th>{t("employee")}</th><th>{t("payBasic")}</th><th className="hide-sm">{t("payAllowances")}</th><th className="hide-sm">{t("payInsured")}</th><th /></tr></thead>
        <tbody>{shown.map((p) => {
          const cur = current(p);
          const basic = cur.filter((i) => elById.get(i.element_id)?.key === "basic").reduce((a, i) => a + Number(i.amount), 0);
          const allow = cur.filter((i) => elById.get(i.element_id)?.key !== "basic" && elById.get(i.element_id)?.kind === "earning").reduce((a, i) => a + Number(i.amount), 0);
          const prof = profiles.get(p.employment_id);
          const insured = prof ? prof.insured : p.nationality === "EG";
          return [
            <tr key={p.employment_id}>
              <td><span className="strong">{pick(p.full_name, p.full_name_ar)}</span><br /><span className="muted small-note">{p.employee_number} · {pick(p.job_title ?? "", p.job_title_ar)}</span></td>
              <td className="mono">{basic ? money(basic) : <span className="badge warning">{t("payNotSet")}</span>}</td>
              <td className="hide-sm mono">{allow ? money(allow) : "—"}</td>
              <td className="hide-sm">{insured ? t("yes") : t("no")}</td>
              <td className="cell-actions"><button type="button" className="btn secondary small" aria-expanded={open === p.employment_id}
                onClick={() => setOpen(open === p.employment_id ? null : p.employment_id)}>{canEdit ? t("edit") : t("payView")}</button></td>
            </tr>,
            open === p.employment_id && (
              <tr key={`${p.employment_id}-edit`}><td colSpan={5}>
                <PayEditor ws={ws} person={p} items={items.filter((i) => i.employment_id === p.employment_id)} elements={elements}
                  profile={prof} canEdit={canEdit} onSaved={refresh} />
              </td></tr>
            ),
          ];
        })}</tbody>
      </table></div>
    </section>
  );
}

function PayEditor({ ws, person, items, elements, profile, canEdit, onSaved }: {
  ws: Workspace; person: DirectoryRow; items: PayItem[]; elements: PayElement[]; profile: PayrollProfile | undefined; canEdit: boolean; onSaved: () => Promise<void>;
}) {
  const { t, pick } = useI18n();
  const now = today();
  const recurring = elements.filter((e) => e.recurring && e.active);
  const amountOn = (elId: string, day: string) => items.find((i) => i.element_id === elId && i.start_date <= day && (!i.end_date || i.end_date >= day))?.amount ?? 0;
  const ref = person.hire_date > now ? person.hire_date : now;
  const [amounts, setAmounts] = useState<Record<string, string>>(Object.fromEntries(recurring.map((e) => [e.id, String(amountOn(e.id, ref) || "")])));
  const [from, setFrom] = useState(person.hire_date > now ? person.hire_date : now.slice(0, 8) + "01");
  const [prof, setProf] = useState<PayrollProfile>(profile ?? {
    employment_id: person.employment_id, insured: person.nationality === "EG", insurance_number: null, insurance_salary: null,
    payment_method: "bank", bank_name: null, bank_account: null, tax_exempt: false,
  });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  async function save(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    try {
      for (const el of recurring) {
        const next = Number(amounts[el.id] || 0);
        if (next !== Number(amountOn(el.id, from))) await setPayItem(person.employment_id, el.id, next, from);
      }
      await savePayrollProfile(ws.membership.company_id, prof);
      setMsg({ ok: true, text: t("saved") });
      await onSaved();
    } catch (err) { setMsg({ ok: false, text: errText(err, t("errorGeneric")) }); }
  }

  const history = [...items].sort((a, b) => b.start_date.localeCompare(a.start_date));
  return (
    <form className="stack pay-editor" onSubmit={save}>
      <fieldset disabled={!canEdit} className="stack">
        <div className="field-row">
          {recurring.map((el) => (
            <label key={el.id} className="field"><span>{pick(el.name_en, el.name_ar)}</span>
              <input type="number" min="0" step="0.01" inputMode="decimal" value={amounts[el.id] ?? ""} onChange={(e) => setAmounts({ ...amounts, [el.id]: e.target.value })} /></label>
          ))}
        </div>
        <label className="field"><span>{t("payFrom")}</span><input type="date" value={from} min={person.hire_date} onChange={(e) => setFrom(e.target.value)} required /></label>
        <div className="field-row">
          <label className="check"><input type="checkbox" checked={prof.insured} onChange={(e) => setProf({ ...prof, insured: e.target.checked })} /> {t("payInsuredLong")}</label>
          <label className="check"><input type="checkbox" checked={prof.tax_exempt} onChange={(e) => setProf({ ...prof, tax_exempt: e.target.checked })} /> {t("payTaxExempt")}</label>
        </div>
        <div className="field-row">
          <label className="field"><span>{t("payInsuranceNo")}</span><input value={prof.insurance_number ?? ""} onChange={(e) => setProf({ ...prof, insurance_number: e.target.value || null })} /></label>
          <label className="field"><span>{t("payInsuranceSalary")}</span><input type="number" min="0" step="0.01" placeholder={t("payAuto")} value={prof.insurance_salary ?? ""}
            onChange={(e) => setProf({ ...prof, insurance_salary: e.target.value ? Number(e.target.value) : null })} /></label>
          <label className="field"><span>{t("payMethod")}</span>
            <select value={prof.payment_method} onChange={(e) => setProf({ ...prof, payment_method: e.target.value as PayrollProfile["payment_method"] })}>
              <option value="bank">{t("payBank")}</option><option value="cash">{t("payCash")}</option><option value="wallet">{t("payWallet")}</option>
            </select></label>
        </div>
        {prof.payment_method === "bank" && (
          <div className="field-row">
            <label className="field"><span>{t("payBankName")}</span><input value={prof.bank_name ?? ""} onChange={(e) => setProf({ ...prof, bank_name: e.target.value || null })} /></label>
            <label className="field"><span>{t("payAccount")}</span><input dir="ltr" value={prof.bank_account ?? ""} onChange={(e) => setProf({ ...prof, bank_account: e.target.value || null })} /></label>
          </div>
        )}
      </fieldset>
      {msg && <p className={`alert ${msg.ok ? "info" : "danger"}`} role="status">{msg.text}</p>}
      {canEdit && <div className="actions"><button className="btn primary">{t("save")}</button></div>}
      {history.length > 0 && (
        <details>
          <summary>{t("payHistory")}</summary>
          <ul className="plain-list small-note">
            {history.map((i) => {
              const el = elements.find((e) => e.id === i.element_id);
              return <li key={i.id}>{el ? pick(el.name_en, el.name_ar) : ""}: <span className="mono">{money(i.amount)}</span> · <span className="mono">{i.start_date} → {i.end_date ?? "…"}</span></li>;
            })}
          </ul>
        </details>
      )}
    </form>
  );
}

function Elements({ ws, canEdit }: { ws: Workspace; canEdit: boolean }) {
  const { t, pick } = useI18n();
  const cid = ws.membership.company_id;
  const [list, setList] = useState<PayElement[]>([]);
  const [f, setF] = useState({ name_en: "", name_ar: "", kind: "earning" as "earning" | "deduction", taxable: true, insurable: true, recurring: false });
  const [error, setError] = useState<string | null>(null);
  const refresh = () => loadPayElements(cid).then(setList);
  useEffect(() => { void refresh(); }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps
  async function add(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const key = f.name_en.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").replace(/^(\d)/, "x_$1").slice(0, 40) || "element";
    try {
      await savePayElement(cid, { key, name_en: f.name_en, name_ar: f.name_ar || null, kind: f.kind,
        taxable: f.kind === "earning" && f.taxable, insurable: f.kind === "earning" && f.insurable, recurring: f.recurring, sort_order: 150 });
      setF({ name_en: "", name_ar: "", kind: "earning", taxable: true, insurable: true, recurring: false });
      await refresh();
    } catch (err) { setError(errText(err, t("errorGeneric"))); }
  }
  return (
    <>
      <section className="card">
        <h2>{t("payElements")}</h2>
        <div className="table-wrap"><table>
          <thead><tr><th>{t("payElement")}</th><th>{t("payKind")}</th><th className="hide-sm">{t("payTaxable")}</th><th className="hide-sm">{t("payInsurable")}</th><th className="hide-sm">{t("payMonthly")}</th><th>{t("active")}</th></tr></thead>
          <tbody>{list.map((x) => (
            <tr key={x.id}>
              <td>{pick(x.name_en, x.name_ar)}</td>
              <td>{t(x.kind === "earning" ? "payEarning" : "payDeduction")}</td>
              <td className="hide-sm">{x.taxable ? t("yes") : t("no")}</td>
              <td className="hide-sm">{x.insurable ? t("yes") : t("no")}</td>
              <td className="hide-sm">{x.recurring ? t("yes") : t("payOneOff")}</td>
              <td><input type="checkbox" aria-label={t("active")} checked={x.active} disabled={!canEdit || x.key === "basic"}
                onChange={async (e) => { await savePayElement(cid, { id: x.id, active: e.target.checked }); await refresh(); }} /></td>
            </tr>
          ))}</tbody>
        </table></div>
        <p className="muted small-note">{t("payElementsHelp")}</p>
      </section>
      {canEdit && (
        <form className="card" onSubmit={add}>
          <h2>{t("payNewElement")}</h2>
          <div className="field-row">
            <label className="field"><span>{t("nameEn")}</span><input value={f.name_en} onChange={(e) => setF({ ...f, name_en: e.target.value })} required /></label>
            <label className="field"><span>{t("nameAr")}</span><input dir="rtl" value={f.name_ar} onChange={(e) => setF({ ...f, name_ar: e.target.value })} /></label>
            <label className="field"><span>{t("payKind")}</span>
              <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as "earning" | "deduction" })}>
                <option value="earning">{t("payEarning")}</option><option value="deduction">{t("payDeduction")}</option>
              </select></label>
          </div>
          <div className="row">
            {f.kind === "earning" && <>
              <label className="check"><input type="checkbox" checked={f.taxable} onChange={(e) => setF({ ...f, taxable: e.target.checked })} /> {t("payTaxable")}</label>
              <label className="check"><input type="checkbox" checked={f.insurable} onChange={(e) => setF({ ...f, insurable: e.target.checked })} /> {t("payInsurable")}</label>
            </>}
            <label className="check"><input type="checkbox" checked={f.recurring} onChange={(e) => setF({ ...f, recurring: e.target.checked })} /> {t("payMonthly")}</label>
          </div>
          {error && <p className="alert danger" role="alert">{error}</p>}
          <div className="actions"><button className="btn secondary">{t("payAdd")}</button></div>
        </form>
      )}
    </>
  );
}

function PayrollSettings({ ws }: { ws: Workspace }) {
  const { t } = useI18n();
  const cid = ws.membership.company_id;
  const [f, setF] = useState({ levels: 1, uhi: false, payDay: 25 });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => {
    void Promise.all([getSetting(cid, "payroll.approval_levels"), getSetting(cid, "payroll.universal_health_insurance"), getSetting(cid, "payroll.pay_day")])
      .then(([l, u, d]) => setF({ levels: Number(l ?? 1), uhi: u === true, payDay: Number(d ?? 25) }));
  }, [cid]);
  async function save(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    try {
      await saveSetting(cid, "payroll.approval_levels", f.levels);
      await saveSetting(cid, "payroll.universal_health_insurance", f.uhi);
      await saveSetting(cid, "payroll.pay_day", f.payDay);
      setMsg({ ok: true, text: t("saved") });
    } catch (err) { setMsg({ ok: false, text: errText(err, t("errorGeneric")) }); }
  }
  return (
    <form className="card" onSubmit={save}>
      <h2>{t("paySettings")}</h2>
      <div className="field-row">
        <label className="field"><span>{t("payApprovalLevels")}</span>
          <select value={f.levels} onChange={(e) => setF({ ...f, levels: Number(e.target.value) })}>
            <option value={1}>{t("payOneApprover")}</option><option value={2}>{t("payTwoApprovers")}</option>
          </select></label>
        <label className="field"><span>{t("payPayDay")}</span><input type="number" min={1} max={28} value={f.payDay} onChange={(e) => setF({ ...f, payDay: Number(e.target.value) })} /></label>
      </div>
      <label className="check"><input type="checkbox" checked={f.uhi} onChange={(e) => setF({ ...f, uhi: e.target.checked })} /> {t("payUhi")}</label>
      <p className="muted small-note">{t("payUhiHelp")}</p>
      {msg && <p className={`alert ${msg.ok ? "info" : "danger"}`} role="status">{msg.text}</p>}
      <div className="actions"><button className="btn secondary">{t("save")}</button></div>
    </form>
  );
}
